// Kundeordre (/order-details/:id), i startsidens stil (planer/ny-kundesamhandling-plan.md):
// kunden øverst (lenke til kundekortet), SMS-er til kunden, «Skriv ut etikett» og «Send SMS»,
// og skjemaet for å endre ordren. «Lagre endringer» går til kundekortet etterpå, som før.
// Skiplukk (type «skiplukk», src/skiplukk/felles.js): skipar og oppfølger i stedet for vare, og
// «Gjør om til service», som avslutter skiplukket og åpner skiservice i butikkmodus.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import OrdreMeldinger from './components/OrdreMeldinger';
import { escapeHtml } from './utils/format';
import SmsTilKunden from './components/SmsTilKunden';
import UtsettOrdreVarsel from './components/UtsettOrdreVarsel';
import { erGammel } from './utils/mineOrdrer';
import formatDateTime from './hjelpere/formatDateTime.js';
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, Valgknapper, ORDRESTATUSER, knappPrimar, knappSekundar } from './ny-kundesamhandling/Skjema';
import Ikon from './ny-kundesamhandling/Ikon';
import KundeBanner from './components/KundeBanner';
import KlubbKontaktBanner, { KlubbMerke, kontaktSomMottaker } from './components/KlubbKontaktBanner';
import Cookies from 'js-cookie';
import { apneButikkmodus } from './utils/butikkmodus';
import ParSkjema from './skiplukk/ParSkjema';
import { plukklisteHtml } from './skiplukk/plukkliste';
import OrdreTilbud from './tilbud/OrdreTilbud';
import OrdrePris from './tilbud/OrdrePris';
import OrdreServicer from './tilbud/OrdreServicer';
import { BRUK, erSkiplukk, fraOrdre, tilLagring, tomtPar, skiserviceSti, erNyttForOppfolger, manglerFelt } from './skiplukk/felles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Registreringstidspunktet ligger i MongoDB-ID-en (som ServiceCustomerInfo).
function opprettetFraId(objectId) {
    if (!objectId) return null;
    return new Date(parseInt(String(objectId).substring(0, 8), 16) * 1000);
}

function OrderDetails() {
    const { id } = useParams(); // Henter _id fra URL (MongoDB ObjectId)
    const navigate = useNavigate(); // For navigering
    // Fra «Registrer ordre»: ble SMS-en med kundelenken sendt? (CreateOrder.js)
    const smsLenke = useLocation().state?.smsLenke || null;
    const [formData, setFormData] = useState({
        Varemerke: '',
        Produkt: '',
        Størrelse: '',
        Farge: '',
        Status: '',
        Kommentar: '',
        Ansatt: '',
    });
    const [customer, setCustomer] = useState(null); // For å holde kundedetaljer
    const [orderDetails, setOrderDetails] = useState(null); // For å holde ordredetaljer fra API
    const [employees, setEmployees] = useState([]); // For å holde ansatte data
    const [updateMessage, setUpdateMessage] = useState(''); // For å vise oppdateringsmelding
    const [feil, setFeil] = useState('');
    const [lagrer, setLagrer] = useState(false);
    const [ikkeFunnet, setIkkeFunnet] = useState(false);
    const [par, setPar] = useState(() => [tomtPar()]);
    const [oppfolger, setOppfolger] = useState('');
    const [bekreftService, setBekreftService] = useState(false);
    const [tilService, setTilService] = useState(false);
    const [bekreftTrykk, setBekreftTrykk] = useState(false);
    const [tilTrykk, setTilTrykk] = useState(false);

    // Hent ordredetaljer
    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/orders/${id}`);
                if (response.ok) {
                    const order = await response.json();
                    setOrderDetails(order); // Setter ordredata til state
                    setFormData({
                        Varemerke: order.Varemerke || '',
                        Produkt: order.Produkt || '',
                        Størrelse: order.Størrelse || '',
                        Farge: order.Farge || '',
                        Status: order.Status || 'Aktiv',
                        Kommentar: order.Kommentar || '',
                        Ansatt: order.Ansatt || '',
                    });
                    setPar(fraOrdre(order));
                    setOppfolger(order.Oppfolger || '');

                    // Oppfølgeren har åpnet skiplukket: ingen varsel ved innlogging lenger.
                    const ansatt = Cookies.get('selectedEmployee') || '';
                    if (erNyttForOppfolger(order, ansatt)) {
                        fetch(`${API_BASE_URL}/orders/${order._id}/oppfolger-sett`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ ansatt }),
                        }).catch(() => {});
                    }

                    // Hent kundedetaljer basert på kundeid fra ordren
                    if (order.kundeid) {
                        fetchCustomer(order.kundeid);
                    }
                } else {
                    console.error('Ordre ble ikke funnet');
                    setIkkeFunnet(true);
                }
            } catch (error) {
                console.error('Feil ved henting av ordren:', error);
                setFeil('Klarte ikke å hente ordren. Last siden på nytt om litt.');
            }
        };

        const fetchCustomer = async (kundeid) => {
            try {
                const response = await fetch(`${API_BASE_URL}/customers?customerNumber=${encodeURIComponent(kundeid)}`);
                if (response.ok) {
                    const customerData = await response.json(); // Forventer en liste av kunder
                    const customer = customerData.find((c) => c.customerNumber === kundeid);

                    if (customer) {
                        setCustomer(customer);
                    } else {
                        console.error('Kunde med dette customerNumber ble ikke funnet');
                    }
                } else {
                    console.error('API-svaret var ikke vellykket');
                }
            } catch (error) {
                console.error('Feil ved henting av kunden:', error);
            }
        };

        fetchOrder();
    }, [id]);

    // Hent ansatte fra databasen
    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/employees`);
                if (response.ok) {
                    setEmployees(await response.json());
                } else {
                    console.error('Feil ved henting av ansatte');
                }
            } catch (error) {
                console.error('Feil ved kommunikasjon med serveren:', error);
            }
        };

        fetchEmployees();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setUpdateMessage('');
    };

    const skiplukk = erSkiplukk(orderDetails);

    const saveOrder = async () => {
        if (skiplukk) {
            const mangler = manglerFelt(par) || (!String(formData.Kommentar || '').trim() ? 'Skriv en beskjed til den som følger opp.' : '');
            if (mangler) {
                setFeil(mangler);
                return false;
            }
        }
        const updatedOrder = {
            ...formData,
            ...(skiplukk && {
                Varemerke: 'Skiplukk',
                Produkt: par.map((p) => BRUK.find((b) => b.verdi === p.bruk)?.tittel).filter(Boolean).join(' + ') || 'Ski',
                Oppfolger: oppfolger,
                skiplukk: tilLagring(par),
            }),
            Endretdato: new Date().toLocaleString('no-NO', { timeZone: 'Europe/Oslo' }),
        };

        setFeil('');
        try {
            const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedOrder),
            });

            if (response.ok) {
                setUpdateMessage(skiplukk ? 'Skiplukket er oppdatert' : 'Ordren er oppdatert');
                return true; // Returner true hvis lagring var vellykket
            }
            console.error('Feil ved oppdatering av ordre:', response.status);
            setFeil('Klarte ikke å lagre ordren. Prøv igjen.');
            return false;
        } catch (error) {
            console.error('Feil ved kommunikasjon med serveren:', error);
            setFeil('Fikk ikke kontakt med serveren. Prøv igjen.');
            return false;
        }
    };

    // Klubbestilling fra klubbinfo (vinnsel, 03.11): står på klubben og en kontaktperson der, ikke
    // på en kunde. «mottaker» er kunden eller kontaktpersonen, til SMS, etikett og prisoversikt.
    const kontaktKlubb = orderDetails?.kontaktKlubb || null;
    const mottaker = customer || kontaktSomMottaker(kontaktKlubb);

    // «Send pristilbud til kontaktpersonen» i klubbinfo lenker hit med #ot-tilbud.
    const harOrdre = !!orderDetails?._id;
    useEffect(() => {
        if (!harOrdre || window.location.hash !== '#ot-tilbud') return;
        document.getElementById('ot-tilbud')?.scrollIntoView({ block: 'start' });
    }, [harOrdre]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (lagrer) return;
        setLagrer(true);
        const ok = await saveOrder();
        setLagrer(false);

        // Naviger til kundedetaljer etter oppdatering
        if (ok && customer) {
            navigate(`/customer-details/${customer._id}`);
        }
    };

    const handlePrintLabel = async () => {
        const savedSuccessfully = await saveOrder();

        if (savedSuccessfully && mottaker && orderDetails) {
            const printWindow = window.open('', '', 'width=500,height=300');

            const today = new Date();
            const formattedDate = `${today.getDate().toString().padStart(2, '0')}.${(
                today.getMonth() + 1
            )
                .toString()
                .padStart(2, '0')}.${today.getFullYear()}`;

            printWindow.document.write(`
        <html>
          <head>
            <style>
              @page {
                size: 90mm 29mm;
                margin: 0;
              }
              @media print {
                body {
                  width: 90mm;
                  height: 29mm;
                  margin: 0;
                  padding: 0;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  font-family: Arial, sans-serif;
                }
              }
              .customer-name {
                font-size: 10px;
                text-align: center;
              }
              .order-id {
                font-size: 20px;
                text-align: center;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="customer-name">${kontaktKlubb ? `${escapeHtml(kontaktKlubb.klubbNavn)}: ` : ''}${escapeHtml(mottaker.firstName)} ${escapeHtml(mottaker.lastName)} (${formattedDate})</div>
            <div class="order-id">${skiplukk ? 'Skiplukk' : 'Ordrenummer'}: ${escapeHtml(String(orderDetails.ordreid ?? ''))}</div>
          </body>
        </html>
      `);
            printWindow.document.close();
            printWindow.focus();

            printWindow.print();
            printWindow.close();
        }
    };

    // «Gjør om til service»: lagrer endringene, merker ordren/skiplukket som overført og åpner
    // butikkmodus for kunden, som «Registrer service» (skiservice med parene på et skiplukk).
    // Ordren står aktiv til kunden har hentet og betalt, så servicen kan kobles til den
    // (tilbud/OrdreServicer.jsx; bare aktive ordrer og servicer kan kobles).
    const gjorOmTilService = async () => {
        if (tilService || !customer) return;
        setTilService(true);
        setFeil('');
        try {
            if (!(await saveOrder())) { setTilService(false); return; }
            const r = await fetch(`${API_BASE_URL}/orders/${id}/til-service`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ansatt: Cookies.get('selectedEmployee') || '' }),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setOrderDetails(await r.json());
            await apneButikkmodus(customer, skiplukk ? skiserviceSti(par) : '/');
        } catch (e) {
            console.error('Feil ved «Gjør om til service»:', e);
            setFeil(`Klarte ikke å gjøre ${skiplukk ? 'skiplukket' : 'ordren'} om til service. Prøv igjen.`);
            setTilService(false);
        }
    };

    // «Overfør til trykk» (klubbbestilling som skal trykkes med logo, vinnsel 02.11): lagrer endringene,
    // lager en trykkjobb med varen og prislinjene og åpner den. Ordren og trykket er koblet og
    // avsluttes sammen (src/lib/ordreTrykk.js).
    const overforTilTrykk = async () => {
        if (tilTrykk) return;
        setTilTrykk(true);
        setFeil('');
        try {
            if (!(await saveOrder())) { setTilTrykk(false); return; }
            const r = await fetch(`${API_BASE_URL}/orders/${id}/til-trykk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ansatt: Cookies.get('selectedEmployee') || '' }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
            navigate(`/service-details-club/${data.trykk._id}`, { state: { fraOrdre: orderDetails?.ordreid } });
        } catch (e) {
            console.error('Feil ved «Overfør til trykk»:', e.message);
            setFeil('Klarte ikke å overføre ordren til trykk. Prøv igjen.');
            setTilTrykk(false);
        }
    };

    // Plukkliste (A4) for skiplukket: lagrer først, som etiketten, så utskriften viser det som står
    // i skjemaet. HTML-en bygges med escaping i skiplukk/plukkliste.js.
    const handlePrintPlukkliste = async () => {
        if (!(await saveOrder())) return;
        const vindu = window.open('', '', 'width=900,height=1100');
        if (!vindu) {
            setFeil('Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for denne siden og prøv igjen.');
            return;
        }
        vindu.document.write(plukklisteHtml({
            ordre: orderDetails,
            kunde: customer,
            par,
            ansvarlig: formData.Ansatt,
            oppfolger,
            kommentar: formData.Kommentar,
            butikk: orderDetails?.butikkid === 2 ? 'Sport 1 Slemmestad' : orderDetails?.butikkid === 1 ? 'Sport 1 Røyken' : '',
        }));
        vindu.document.close();
        vindu.focus();
        vindu.onafterprint = () => vindu.close();
        vindu.print();
    };

    // Funksjon for å navigere til SendSMS.js
    const handleSendSMS = () => {
        // Naviger til SendSMS.js med ordre- og kundedetaljer
        navigate('/sendsms', { state: { orderDetails, customer: mottaker } });
    };

    if (ikkeFunnet) {
        return (
            <div className="mx-auto max-w-7xl space-y-3">
                <Tilbake til="/ordre">Tilbake til ordrer</Tilbake>
                <h1 className="text-xl font-bold text-slate-900">Fant ikke ordren</h1>
            </div>
        );
    }

    const registrert = opprettetFraId(orderDetails?._id);
    const endret = orderDetails?.Endretdato || orderDetails?.endretdato;
    const kundeNavn = customer ? [customer.firstName, customer.lastName].filter(Boolean).join(' ') : '';

    return (
        <div className="mx-auto max-w-7xl space-y-4 pb-10">
            {customer ? <Tilbake til={`/customer-details/${customer._id}`}>Tilbake til kundekortet</Tilbake>
                : kontaktKlubb ? <Tilbake til={`/klubber/${kontaktKlubb.klubbId}`}>Tilbake til klubben</Tilbake>
                : <Tilbake til="/ordre">Tilbake til ordrer</Tilbake>}

            {skiplukk ? (
                <Sidetopp
                    ikon="snofnugg"
                    farge="lager"
                    tittel={`Skiplukk ${orderDetails.ordreid ?? ''}`}
                    undertittel={[
                        'Kunden trenger hjelp til å finne ski (ikke en vareordre)',
                        orderDetails.Ansatt && `ansvarlig: ${orderDetails.Ansatt}`,
                        orderDetails.Oppfolger ? `følges opp av ${orderDetails.Oppfolger}` : 'ingen oppfølger valgt',
                    ].filter(Boolean).join(' · ')}
                />
            ) : (
                <Sidetopp
                    ikon="kvittering"
                    farge="betaling"
                    tittel={orderDetails ? `${kontaktKlubb ? 'Klubbordre' : 'Ordre'} ${orderDetails.ordreid ?? ''}` : 'Kundeordre'}
                    undertittel={orderDetails ? [orderDetails.Varemerke, orderDetails.Produkt].filter(Boolean).join(' ') : 'Henter ordren …'}
                >
                    {kontaktKlubb && <KlubbMerke klubbNavn={kontaktKlubb.klubbNavn} />}
                </Sidetopp>
            )}

            {orderDetails?.overfortTilService?.dato && (
                <p className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-900">
                    <Ikon navn="hake" className="mt-0.5 w-5 h-5 shrink-0" />
                    <span>
                        Gjort om til service {formatDateTime(orderDetails.overfortTilService.dato)}
                        {orderDetails.overfortTilService.av ? ` av ${orderDetails.overfortTilService.av}` : ''}. Koble servicen under, og avslutt {skiplukk ? 'skiplukket' : 'ordren'} når kunden har hentet og betalt.
                    </span>
                </p>
            )}

            {orderDetails?.trykk?.id && (
                <p className="flex flex-wrap items-start gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-violet-950">
                    <Ikon navn="genser" className="mt-0.5 w-5 h-5 shrink-0" />
                    <span className="min-w-0 flex-1">
                        Overført til{' '}
                        <Link to={`/service-details-club/${orderDetails.trykk.id}`} className="font-semibold text-indigo-700 underline underline-offset-2">
                            trykk {orderDetails.trykk.serviceid}
                        </Link>{' '}
                        {formatDateTime(orderDetails.trykk.dato)}{orderDetails.trykk.av ? ` av ${orderDetails.trykk.av}` : ''}.
                        Ordren og trykket avsluttes sammen når varene er levert.
                    </span>
                </p>
            )}

            {/* Varsel om gammel ordre (vinnsel 29.10): bare når varselet er aktivt, dvs. ordren er gammel
                eller varselet er utsatt (så datoen kan endres). Lagret status, ikke skjemaet. */}
            {orderDetails && orderDetails.Status !== 'Avsluttet' && (orderDetails.utsatt || erGammel(orderDetails)) && (
                <UtsettOrdreVarsel ordre={orderDetails} onEndret={(d) => setOrderDetails((o) => ({ ...o, ...d }))} />
            )}

            {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

            {/* Kunden og datoene (som ServiceCustomerInfo, med lenke til kundekortet) */}
            {orderDetails && customer && (
                <KundeBanner
                    kundeId={customer._id}
                    navn={kundeNavn}
                    telefon={customer.phoneNumber}
                    epost={customer.email}
                    linje={<>Registrert {registrert ? formatDateTime(registrert) : 'ukjent dato'}{endret && <> · endret {String(endret)}</>}</>}
                />
            )}

            {orderDetails && kontaktKlubb && (
                <KlubbKontaktBanner
                    kontakt={kontaktKlubb}
                    linje={<>Registrert {registrert ? formatDateTime(registrert) : 'ukjent dato'}{endret && <> · endret {String(endret)}</>}</>}
                />
            )}

            <SmsTilKunden telefon={mottaker?.phoneNumber} />

            <div className={`grid gap-2 ${skiplukk ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {skiplukk && (
                    <button type="button" onClick={handlePrintPlukkliste} disabled={!orderDetails} className={knappSekundar}>
                        <Ikon navn="dokument" className="w-5 h-5" />
                        Skriv ut plukkliste
                    </button>
                )}
                <button type="button" onClick={handlePrintLabel} disabled={!orderDetails || !mottaker} className={knappSekundar}>
                    <Ikon navn="lapp" className="w-5 h-5" />
                    Skriv ut etikett{orderDetails?.ordreid != null ? ` (ordrenr. ${orderDetails.ordreid})` : ''}
                </button>
                <button type="button" onClick={handleSendSMS} disabled={!orderDetails} className={knappSekundar}>
                    <Ikon navn="send" className="w-5 h-5" />
                    Send SMS
                </button>
            </div>

            {/* Skjemaet for ordreoppdatering */}
            <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-3">
                <h2 className="text-base font-bold text-slate-900">{skiplukk ? 'Skiplukket' : 'Ordren'}</h2>
                {skiplukk ? (
                    <ParSkjema idPrefiks="skiplukk" par={par} onEndre={(p) => { setPar(p); setUpdateMessage(''); }} kundeNavn={kundeNavn} />
                ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                    <Tekstfelt id="ordre-merke" etikett="Varemerke" name="Varemerke" value={formData.Varemerke} onChange={handleChange} required />
                    <Tekstfelt id="ordre-produkt" etikett="Produkt" name="Produkt" value={formData.Produkt} onChange={handleChange} required />
                    {/* Klubbordre: mange produkter, størrelse og farge står på varelinjene, så feltene er valgfrie. */}
                    <Tekstfelt id="ordre-str" etikett={kontaktKlubb ? 'Størrelse (valgfritt)' : 'Størrelse'} name="Størrelse" value={formData.Størrelse} onChange={handleChange} required={!kontaktKlubb} />
                    <Tekstfelt id="ordre-farge" etikett={kontaktKlubb ? 'Farge (valgfritt)' : 'Farge'} name="Farge" value={formData.Farge} onChange={handleChange} required={!kontaktKlubb} />
                </div>
                )}
                <Valgknapper
                    id="ordre-status"
                    etikett="Status"
                    valg={skiplukk ? ORDRESTATUSER.filter((v) => v.verdi !== 'AktivKLUBB') : ORDRESTATUSER}
                    verdi={formData.Status}
                    onVelg={(verdi) => handleChange({ target: { name: 'Status', value: verdi } })}
                />
                {orderDetails?.trykk?.id && formData.Status === 'Avsluttet' && orderDetails.Status !== 'Avsluttet' && (
                    <p role="status" className="text-sm font-semibold text-violet-900">Trykk {orderDetails.trykk.serviceid} avsluttes også når du lagrer.</p>
                )}
                <Tekstfelt id="ordre-kommentar" etikett={skiplukk ? 'Beskjed og notater' : 'Kommentar'} flerlinjet name="Kommentar" value={formData.Kommentar} onChange={handleChange} required={skiplukk} />
                <Valgfelt id="ordre-ansatt" etikett={skiplukk ? 'Ansvarlig' : 'Ansatt'} name="Ansatt" value={formData.Ansatt} onChange={handleChange}>
                    <option value="">Velg ansatt</option>
                    {/* Ordrens ansatt kan mangle i lista (f.eks. sluttet); vis den likevel, så feltet ikke ser tomt ut. */}
                    {formData.Ansatt && !employees.some((e) => e.navn === formData.Ansatt) && (
                        <option value={formData.Ansatt}>{formData.Ansatt}</option>
                    )}
                    {employees.map((employee) => (
                        <option key={employee._id} value={employee.navn}>
                            {employee.navn}
                        </option>
                    ))}
                </Valgfelt>
                {skiplukk && (
                    <div>
                        <Valgfelt id="skiplukk-oppfolger" etikett="Følges opp av" value={oppfolger} onChange={(e) => { setOppfolger(e.target.value); setUpdateMessage(''); }} aria-describedby="skiplukk-oppfolger-hjelp">
                            <option value="">Ingen valgt</option>
                            {oppfolger && !employees.some((e) => e.navn === oppfolger) && <option value={oppfolger}>{oppfolger}</option>}
                            {employees.map((employee) => (
                                <option key={employee._id} value={employee.navn}>{employee.navn}</option>
                            ))}
                        </Valgfelt>
                        <p id="skiplukk-oppfolger-hjelp" className="mt-1 text-sm text-slate-600">
                            Den ansatte får beskjed når de logger inn. Skiplukket står under «Dine aktive ordrer» både hos den
                            ansvarlige og hos den som følger opp.
                        </p>
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                    <button type="submit" disabled={lagrer || !orderDetails} className={knappPrimar}>
                        <Ikon navn="hake" className="w-6 h-6" />
                        {lagrer ? 'Lagrer …' : 'Lagre endringer'}
                    </button>
                    <p role="status" className="font-medium text-emerald-800">{updateMessage}</p>
                </div>
            </form>

            {/* Under ordren (vinnsel 29.10): meldinger med kunden og kundelenken (src/components/OrdreMeldinger.jsx) */}
            {/* Kundelenken bygger på kundekortet, så den er ikke på klubbestillinger. Pristilbudet går
                til kontaktpersonen i klubben (mottaker). */}
            {orderDetails && !kontaktKlubb && (
                <OrdreMeldinger
                    ordre={orderDetails}
                    kunde={customer}
                    smsLenke={smsLenke}
                    onOppdatert={(o) => setOrderDetails((d) => ({ ...d, ...o }))}
                />
            )}


            {/* Pris til kunden og prisoversikt til kassa (tilbud/OrdrePris.jsx). Linjene er også pristilbudet. */}
            {orderDetails && (
                <OrdrePris
                    ordre={orderDetails}
                    kunde={mottaker}
                    par={par}
                    onOrdreEndret={(o) => setOrderDetails((d) => ({ ...d, ...o }))}
                />
            )}

            {/* Pristilbud fra «Pris til kunden» (tilbud/OrdreTilbud.jsx, som på tekstiltrykk). Ferdigmelding
                sendes med SMS-knappen øverst. */}
            {orderDetails && (
                <OrdreTilbud
                    ordre={orderDetails}
                    kunde={mottaker}
                    klubb={kontaktKlubb}
                    onTilbud={(tilbud) => setOrderDetails((d) => ({ ...d, tilbud }))}
                />
            )}

            {/* Servicen varene hører til, begge veier (tilbud/OrdreServicer.jsx). */}
            {orderDetails?._id && (
                <OrdreServicer
                    ordre={orderDetails}
                    onOrdreEndret={(o) => setOrderDetails((d) => ({ ...d, ...o }))}
                />
            )}

            {/* Klubbbestilling som skal trykkes med logo: overfør til trykk (vinnsel 02.11). */}
            {orderDetails && !skiplukk && orderDetails.Status !== 'Avsluttet' && !orderDetails.trykk?.id && (
                <section aria-labelledby="ordre-trykk" className="space-y-2 rounded-2xl border-2 border-violet-200 bg-violet-50 p-3">
                    <h2 id="ordre-trykk" className="text-base font-bold text-slate-900">Skal varene trykkes med logo?</h2>
                    <p className="text-slate-800">
                        Overfør bestillingen til trykk. Det lages en trykkjobb med varen og prisene fra ordren, og den åpnes så du kan
                        skrive hva som skal trykkes. Ordren og trykket er koblet og avsluttes sammen når varene er levert.
                    </p>
                    {!bekreftTrykk ? (
                        <button type="button" onClick={() => setBekreftTrykk(true)} className={knappPrimar}>
                            <Ikon navn="genser" className="w-6 h-6" /> Overfør til trykk
                        </button>
                    ) : (
                        <div role="group" aria-labelledby="ordre-trykk-bekreft" className="space-y-2 rounded-xl border border-violet-300 bg-white p-2.5">
                            <p id="ordre-trykk-bekreft" className="font-semibold text-slate-900">Lage trykkjobb fra ordre {orderDetails.ordreid}?</p>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={overforTilTrykk} disabled={tilTrykk} className={knappPrimar} autoFocus>
                                    {tilTrykk ? 'Overfører …' : 'Ja, overfør til trykk'}
                                </button>
                                <button type="button" onClick={() => setBekreftTrykk(false)} disabled={tilTrykk} className={knappSekundar}>Avbryt</button>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* Verkstedet skal montere, preparere, slipe o.l.: gjør ordren/skiplukket om til service. */}
            {orderDetails && !kontaktKlubb && orderDetails.Status !== 'Avsluttet' && !orderDetails.overfortTilService?.dato && (
                <section aria-labelledby="skiplukk-service" className="space-y-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3 sm:p-3">
                    <h2 id="skiplukk-service" className="text-base font-bold text-slate-900">
                        {skiplukk ? 'Skal verkstedet gjøre noe med skiene?' : 'Skal verkstedet gjøre noe med varen?'}
                    </h2>
                    {skiplukk ? (
                        <p className="text-slate-800">
                            Når skiene er funnet og verkstedet skal montere bindinger, preparere eller slipe, gjør du skiplukket om til
                            service. Skiservice åpnes i butikkmodus for kunden, som «Registrer service», med ett
                            produkt per par{par.some((p) => p.grunnprepp) ? ' og «Grunnprepp nye ski» valgt der kunden ønsker det' : ''}.
                            Koble servicen til skiplukket etterpå, så vises alt samlet på servicefakturaen.
                        </p>
                    ) : (
                        <p className="text-slate-800">
                            Skal verkstedet montere, justere eller klargjøre varen, gjør du ordren om til service. Butikkmodus åpnes for
                            kunden, som «Registrer service». Koble servicen til ordren etterpå, så vises varen og verkstedarbeidet samlet
                            på servicefakturaen.
                        </p>
                    )}
                    {!customer ? (
                        <p className="text-slate-700">Venter på kunden …</p>
                    ) : !bekreftService ? (
                        <button type="button" onClick={() => setBekreftService(true)} className={knappPrimar}>
                            <Ikon navn="skiftenokkel" className="w-6 h-6" /> Gjør om til service
                        </button>
                    ) : (
                        <div role="group" aria-labelledby="skiplukk-bekreft" className="space-y-2 rounded-xl border border-emerald-300 bg-white p-2.5">
                            <p id="skiplukk-bekreft" className="font-semibold text-slate-900">
                                Registrere {skiplukk ? 'skiservice' : 'service'} for {kundeNavn || 'kunden'} i butikkmodus?
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={gjorOmTilService} disabled={tilService} className={knappPrimar} autoFocus>
                                    {tilService ? 'Åpner butikkmodus …' : 'Ja, gjør om til service'}
                                </button>
                                <button type="button" onClick={() => setBekreftService(false)} disabled={tilService} className={knappSekundar}>
                                    Avbryt
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

export default OrderDetails;
